import React, { useState, useEffect, useRef } from 'react';
import { Navigation, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import LiveBusMap from './LiveBusMap';
import '../Timeline.css';

const MinimalBusSchedule = ({ route, onToggle, isExpanded, beginPlace, destPlace }) => {
  const [showDirections, setShowDirections] = useState(false);
  const [showAllBuses, setShowAllBuses] = useState(false);

  // Store fixed timestamp when route data changes to keep ETA constant
  const dataReceivedTimeRef = useRef(Date.now());

  useEffect(() => {
    // Update timestamp when route changes
    dataReceivedTimeRef.current = Date.now();
  }, [route.routeId]);

  const dataReceivedTime = dataReceivedTimeRef.current;

  // Transform route data to match the expected format
  const scheduleData = {
    route: route.routeName || 'ROUTE',
    busesRunning: route.beginStop?.arrivalTimes?.length || 0,
    stop: route.beginStop?.name || 'Unknown Stop',
    walkingDistance: `${(route.totalWalkingDistance * 0.000621371).toFixed(1)} mi`,
    nextBuses: []
  };

  // Process arrival times for next buses - show all available buses
  if (route.beginStop?.arrivalTimes && route.destStop?.arrivalTimes) {
    route.beginStop.arrivalTimes.forEach((pickupArrival, index) => {
      const busVehicleId = pickupArrival?.vehicleId;
      const matchingDestArrival = route.destStop.arrivalTimes.find(a =>
        a.vehicleId && String(a.vehicleId) === String(busVehicleId)
      ) || route.destStop.arrivalTimes[index];

      // Calculate pickup minutes - use fixed timestamp to keep it constant
      let pickupMinutes = 0;
      let pickupArrivalTime = null; // Store the actual arrival time

      if (pickupArrival.estimateTime) {
        // Use absolute timestamp if available
        if (typeof pickupArrival.estimateTime === 'string' && pickupArrival.estimateTime.includes('/Date(')) {
          const timestampObj = pickupArrival.estimateTime.match(/\/Date\((\d+)\)\//);
          if (timestampObj && timestampObj[1]) {
            const absoluteTime = parseInt(timestampObj[1], 10);
            pickupMinutes = Math.max(0, Math.floor((absoluteTime - dataReceivedTime) / 60000));
            // Calculate arrival time from absolute timestamp
            const arrivalDate = new Date(absoluteTime);
            pickupArrivalTime = arrivalDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
          }
        }
      } else if (pickupArrival.seconds !== null && pickupArrival.seconds !== undefined) {
        // Calculate from fixed timestamp when data was received (keeps it constant)
        const arrivalTime = dataReceivedTime + (pickupArrival.seconds * 1000);
        pickupMinutes = Math.max(0, Math.floor((arrivalTime - dataReceivedTime) / 60000));
        // Calculate arrival time from fixed timestamp
        const arrivalDate = new Date(arrivalTime);
        pickupArrivalTime = arrivalDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      } else {
        pickupMinutes = pickupArrival.minutes || 0;
        // Calculate arrival time by adding minutes to current time (using fixed timestamp)
        const arrivalTime = dataReceivedTime + (pickupMinutes * 60000);
        const arrivalDate = new Date(arrivalTime);
        pickupArrivalTime = arrivalDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      }

      // Calculate destination ETA as actual time - use fixed timestamp to keep it constant
      let destTime = null;
      if (matchingDestArrival) {
        if (matchingDestArrival.estimateTime) {
          if (typeof matchingDestArrival.estimateTime === 'string' && matchingDestArrival.estimateTime.includes('/Date(')) {
            const timestampObj = matchingDestArrival.estimateTime.match(/\/Date\((\d+)\)\//);
            if (timestampObj && timestampObj[1]) {
              const absoluteTime = parseInt(timestampObj[1], 10);
              const destDate = new Date(absoluteTime);
              destTime = destDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            }
          }
        } else if (matchingDestArrival.seconds !== undefined) {
          // Use fixed timestamp to keep it constant
          const arrivalTime = dataReceivedTime + (matchingDestArrival.seconds * 1000);
          const destDate = new Date(arrivalTime);
          destTime = destDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        } else if (matchingDestArrival.minutes !== undefined) {
          // Use fixed timestamp to keep it constant
          const arrivalTime = dataReceivedTime + (matchingDestArrival.minutes * 60000);
          const destDate = new Date(arrivalTime);
          destTime = destDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        }
      }

      scheduleData.nextBuses.push({
        in: pickupMinutes,
        destTime: destTime,
        pickupTime: pickupArrivalTime // Store the constant arrival time
      });
    });
  }

  // Set the first bus data for the main display
  const firstBus = scheduleData.nextBuses[0] || { in: 0, destTime: null, pickupTime: null };
  scheduleData.pickupIn = firstBus.in;
  scheduleData.destTime = firstBus.destTime;
  scheduleData.pickupTime = firstBus.pickupTime;

  // Match map / arrivals: prefer GT map line color from API; name rules are fallback only.
  const apiLineColor = route.routeColor || route.mapLineColor;
  const routeNameUpper = (route.routeName || '').toUpperCase();
  let routeIconBg = '#1E40AF';

  if (typeof apiLineColor === 'string' && apiLineColor.startsWith('#')) {
    routeIconBg = apiLineColor;
  } else if (routeNameUpper.includes('RED')) {
    routeIconBg = '#DC2626';
  } else if (routeNameUpper.includes('BLUE')) {
    routeIconBg = '#1E40AF';
  } else if (routeNameUpper.includes('GREEN')) {
    routeIconBg = '#16A34A';
  } else if (routeNameUpper.includes('EMORY')) {
    routeIconBg = '#EC4899';
  } else if (routeNameUpper.includes('NIGHT') && routeNameUpper.includes('GOLD')) {
    routeIconBg = '#6B7280';
  } else if (routeNameUpper.includes('GOLD') || routeNameUpper.includes('TROLLEY') || routeNameUpper.includes('TECH TROLLEY')) {
    routeIconBg = '#EAB308';
  } else if (routeNameUpper.includes('CLOUGH') || routeNameUpper.includes('PURPLE')) {
    routeIconBg = '#7C3AED';
  } else if (
    routeNameUpper.includes('NORTHSIDE') ||
    (routeNameUpper.includes('ATLANTIC') && !routeNameUpper.includes('WEEKEND ATLANTIC'))
  ) {
    routeIconBg = '#D946EF';
  } else if (routeNameUpper.includes('NASA') || routeNameUpper.includes('SCIENCE')) {
    routeIconBg = '#06B6D4';
  } else if (routeNameUpper.includes('CHARTER')) {
    routeIconBg = '#F97316';
  }

  // Use your app's font family
  const appFontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

  return (
    <div style={{ width: '100%', maxWidth: '42rem', marginBottom: '1rem', fontFamily: appFontFamily, paddingLeft: '0.1rem', paddingRight: '0.1rem' }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '2px solid #cbd5e1',
        overflow: 'hidden',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        marginTop: '2rem'
      }}>
        {/* Header - using route color */}
        <div style={{
          backgroundColor: routeIconBg,
          color: 'white',
          padding: '1.25rem',
          borderBottom: '4px solid #eab308'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ fontSize: '1.875rem', fontWeight: '900', margin: 0, letterSpacing: '-0.02em' }}>{scheduleData.route}</h2>
              <p style={{ fontSize: '0.875rem', opacity: 0.9, marginTop: '0.25rem', margin: 0 }}>{scheduleData.stop}</p>
            </div>
            <div style={{ textAlign: 'right', fontSize: '0.875rem' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                justifyContent: 'flex-end',
                opacity: 0.9
              }}>
                <Navigation size={14} />
                <span>{scheduleData.walkingDistance} walk</span>
              </div>
              <p style={{ opacity: 0.75, marginTop: '0.25rem', margin: 0, textAlign: 'right' }}>{scheduleData.busesRunning} buses active</p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem', paddingLeft: '1rem', paddingRight: '1rem' }}>
          {/* Next bus - highlighted with yellow accent */}
          <div style={{
            background: '#fef9e7',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            marginBottom: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              background: '#fbbf24',
              padding: '10px 6px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '60px'
            }}>
              <div style={{ fontSize: '25px', fontWeight: 'bold', color: '#1f2937', lineHeight: 1 }}>
                {scheduleData.pickupIn}
              </div>
              <div style={{ fontSize: '10px', color: '#78350f', marginTop: '3px', textAlign: 'center' }}>
                min away
              </div>
            </div>
            <div style={{
              flex: 1,
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>Next Bus</div>
                {scheduleData.pickupTime && (
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Picks up {scheduleData.pickupTime}
                  </div>
                )}
              </div>
              {scheduleData.destTime && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', fontWeight: '700' }}>Drop off</div>
                  <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1f2937' }}>
                    ~{scheduleData.destTime}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* "See later buses" link - shown when collapsed */}
          {scheduleData.nextBuses.length > 1 && !showAllBuses && (
            <button
              onClick={() => setShowAllBuses(true)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                padding: '10px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = '0.7';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <ChevronDown size={18} style={{ color: '#2c5282' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c5282' }}>
                See later buses
              </span>
            </button>
          )}

          {/* Following buses - animated expand/collapse */}
          <div style={{
            maxHeight: showAllBuses ? `${scheduleData.nextBuses.length * 150}px` : '0px',
            opacity: showAllBuses ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
          }}>
            {scheduleData.nextBuses.slice(1).map((bus, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                marginBottom: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transform: showAllBuses ? 'translateY(0)' : 'translateY(-8px)',
                transition: `transform 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${idx * 0.05}s, opacity 0.3s ease ${idx * 0.05}s`,
              }}>
                <div style={{
                  background: '#e5e7eb',
                  padding: '10px 6px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '60px'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#6b7280', lineHeight: 1 }}>
                    {bus.in}
                  </div>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px', textAlign: 'center' }}>
                    min away
                  </div>
                </div>
                <div style={{
                  flex: 1,
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                      Next Bus
                    </div>
                    {bus.pickupTime && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Picks up {bus.pickupTime}
                      </div>
                    )}
                  </div>
                  {bus.destTime && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', fontWeight: '700' }}>Drop off</div>
                      <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1f2937' }}>
                        ~{bus.destTime}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* "Hide later buses" link - inside expanded container, at the bottom */}
            <button
              onClick={() => setShowAllBuses(false)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                padding: '6px 0 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = '0.7';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <ChevronUp size={18} style={{ color: '#2c5282' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c5282' }}>
                Hide later buses
              </span>
            </button>
          </div>
        </div>

        {/* Directions toggle - with spacing from header */}
        <div style={{
          padding: '1.25rem',
          borderTop: '2px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          marginTop: '1.5rem'
        }}>
          <button
            onClick={() => setShowDirections(!showDirections)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: '#1e293b',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 900,
              padding: 0,
              fontFamily: appFontFamily,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = '0.7';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '2.5rem',
                height: '2.5rem',
                backgroundColor: '#eab308',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <MapPin size={18} style={{ color: '#1e293b' }} />
              </div>
              <span>
                {showDirections ? 'Hide' : 'Show'} directions
              </span>
            </div>
            <div style={{
              width: '2rem',
              height: '2rem',
              backgroundColor: '#eab308',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {showDirections ? <ChevronUp size={18} style={{ color: '#1e293b' }} /> : <ChevronDown size={18} style={{ color: '#1e293b' }} />}
            </div>
          </button>
        </div>
      </div>

      {/* Expandable Timeline/Directions */}
      {showDirections && (
        <div className="route-details-panel" style={{ marginTop: '1rem' }}>
          <div className="timeline-body">
            <div className="timeline-container">
              {/* Step 1: Walk to Stop */}
              <div className="timeline-step">
                <div className="timeline-node">1</div>
                <div className="timeline-content">
                  <div className="timeline-title">Walk to bus stop</div>
                  <div className="timeline-desc">
                    Stop: {route.beginStop?.name}
                  </div>
                  <div className="timeline-meta">
                    {(route.beginStop?.distance * 0.000621371).toFixed(2)} miles
                  </div>
                  <a
                    href={
                      beginPlace?.coordinates
                        ? `https://www.google.com/maps/dir/?api=1&origin=${beginPlace.coordinates}&destination=${route.beginStop?.latitude},${route.beginStop?.longitude}&travelmode=walking`
                        : `https://www.google.com/maps/dir/?api=1&destination=${route.beginStop?.latitude},${route.beginStop?.longitude}&travelmode=walking`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="timeline-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Get walking directions
                  </a>
                </div>
              </div>

              {/* Step 2: Board the Bus */}
              <div className="timeline-step">
                <div className="timeline-node">2</div>
                <div className="timeline-content">
                  <div className="timeline-title">Ride the bus</div>
                  <div className="timeline-desc">
                    This one's self-explanatory.
                  </div>
                </div>
              </div>

              {/* Step 3: Get Off */}
              <div className="timeline-step">
                <div className="timeline-node">3</div>
                <div className="timeline-content">
                  <div className="timeline-title">Get off at your stop</div>
                  <div className="timeline-desc">

                  </div>
                  {route.destStop?.latitude && route.destStop?.longitude && (
                    <div className="timeline-map-container">
                      <div className="timeline-map-hint">
                        <span className="timeline-map-label">
                          Your stop: {route.destStop?.name}
                        </span>

                      </div>
                      <LiveBusMap
                        routeId={route.routeId}
                        stop={route.destStop}
                        routeColor={routeIconBg}
                      />
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${route.destStop?.latitude},${route.destStop?.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="timeline-map-button"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
                        </svg>
                        Open in Google Maps
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Step 4: Walk to Destination */}
              <div className="timeline-step">
                <div className="timeline-node">4</div>
                <div className="timeline-content">
                  <div className="timeline-title">Walk to your destination</div>
                  <div className="timeline-desc">
                    {destPlace?.name || "Final Destination"}
                  </div>
                  <div className="timeline-meta">
                    {(route.destStop?.distance * 0.000621371).toFixed(2)} miles
                  </div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&origin=${route.destStop?.latitude},${route.destStop?.longitude}&destination=${destPlace?.name}&travelmode=walking`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="timeline-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Get walking directions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default MinimalBusSchedule;
